import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { L, Lang } from '../types/i18n';
interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (value: L) => string;
}
const STORAGE_KEY = 'aj-lang';
const LanguageContext = createContext<LanguageContextValue | null>(null);
function detectInitialLang(): Lang {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'es') return stored;
  return window.navigator.language?.toLowerCase().startsWith('es') ? 'es' : 'en';
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — language still applies for this session */
    }
  }, []);
  const value = useMemo<LanguageContextValue>(() => ({
    lang,
    setLang,
    t: (copy: L) => copy[lang]
  }), [lang, setLang]);
  return <LanguageContext.Provider value={value} data-id="element-328" data-mp="37x37elezxd06">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used inside a LanguageProvider');
  }
  return context;
}